import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Settings, FileText, Upload, Search, Eye, Pencil, Folder, XCircle, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as adminApi from '../../../../api/adminApi';
import { useAdminData } from '../../hooks/useAdminData';
import downloadFile from '../../../../utils/downloadFile';
import styles from './DocumentsSection.module.css';
import metricsStyles from '../statistics/MetricsCards.module.css';
import { Eyebrow, PageHeading } from '../../../../design-system';

export interface DocumentsSectionProps {
  error?: string | null;
  loading?: boolean;
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
}

type DocumentType = 'policy' | 'legal_docs' | 'reports' | 'custom';
type UploadMode = 'single' | 'bulk';
type ActivityType = 'upload' | 'edit' | 'delete' | 'download';

const DOCUMENT_TYPES = [
  { value: 'policy', label: 'Policy Brief' },
  { value: 'legal_docs', label: 'Legal Document' },
  { value: 'reports', label: 'Report' },
  { value: 'custom', label: 'Others' },
];

interface Activity {
  id: string;
  type: ActivityType;
  documentName: string;
  adminName: string;
  timestamp: Date;
  documentId?: number;
}

const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt', '.xlsx', '.xls'];

const TAG_COLORS = [
  { bg: 'rgba(68,76,231,0.13)',  color: 'var(--ds-info-text)', border: 'rgba(68,76,231,0.3)' },
  { bg: 'rgba(97, 114, 243, 0.13)',  color: 'var(--ds-info-text)', border: 'rgba(97, 114, 243, 0.3)' },
  { bg: 'rgba(68, 76, 231, 0.13)',  color: 'var(--ds-link)', border: 'rgba(68, 76, 231, 0.3)' },
  { bg: 'rgba(240, 68, 56, 0.13)',   color: 'var(--ds-danger-text)', border: 'rgba(240, 68, 56, 0.3)'  },
  { bg: 'rgba(97,114,243,0.13)',  color: 'var(--ds-text)', border: 'rgba(97,114,243,0.3)' },
  { bg: 'rgba(164, 188, 253, 0.13)',  color: 'var(--ds-link)', border: 'rgba(164, 188, 253, 0.3)' },
  { bg: 'rgba(240, 68, 56, 0.13)',  color: 'var(--ds-danger-text)', border: 'rgba(240, 68, 56, 0.3)' },
  { bg: 'rgba(97, 114, 243, 0.13)',  color: 'var(--ds-info-text)', border: 'rgba(97, 114, 243, 0.3)' },
];

const getTagColor = (tag: string) => {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) hash = tag.charCodeAt(i) + ((hash << 5) - hash);
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
};

const parseTags = (raw: string): string[] =>
  raw.split(',').map(t => t.trim()).filter(Boolean);

const TagChip: React.FC<{ tag: string; size?: 'sm' | 'md' }> = ({ tag, size = 'sm' }) => {
  const { bg, color, border } = getTagColor(tag);
  return (
    <span style={{
      fontSize: size === 'sm' ? '0.72rem' : '0.8rem',
      background: bg, color, border: `1px solid ${border}`,
      borderRadius: 999,
      padding: size === 'sm' ? '0.05rem 0.45rem' : '0.15rem 0.6rem',
      whiteSpace: 'nowrap', fontWeight: 600, display: 'inline-block',
    }}>
      {tag}
    </span>
  );
};

export const DocumentsSection: React.FC<DocumentsSectionProps> = ({ 
  error: externalError,
  loading: externalLoading,
  onSubmit: externalOnSubmit
}) => {
  const navigate = useNavigate();
  const { admins, adminProfile, secondaryLoading } = useAdminData();

  // Only show documents belonging to the currently logged-in admin
  const currentAdminId = admins.find(a => a.email === adminProfile?.email)?.id;

  const [documents, setDocuments] = useState<adminApi.AdminDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [jurisdictions, setJurisdictions] = useState<adminApi.Jurisdiction[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [recentLimit] = useState(3);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterJurisdiction, setFilterJurisdiction] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [sortBy] = useState('date');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDocs, setSelectedDocs] = useState<number[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<adminApi.AdminDocument | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingType, setEditingType] = useState('');
  const [editingJurisdiction, setEditingJurisdiction] = useState('');
  const [editingDesc, setEditingDesc] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingTags, setEditingTags] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  
  // Upload modal state
  const [uploadMode, setUploadMode] = useState<UploadMode>('single');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadType, setUploadType] = useState<DocumentType>('policy');
  const [uploadCustomType, setUploadCustomType] = useState('');
  const [uploadJurisdiction, setUploadJurisdiction] = useState('');
  const [uploadName, setUploadName] = useState('');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<{ [key: string]: string }>({});
  const [uploadTags, setUploadTags] = useState('');

  // Manage Types modal state
  const [showManageTypesModal, setShowManageTypesModal] = useState(false);
  const [customTypes, setCustomTypes] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('admin_custom_doc_types') || '[]'); }
    catch { return []; }
  });
  const [newTypeName, setNewTypeName] = useState('');
  const [manageTypesError, setManageTypesError] = useState<string | null>(null);
  const [deletingType, setDeletingType] = useState<string | null>(null);
  
  const docsPerPage = 8;

  // Derive unique jurisdiction options from loaded documents
  const jurisdictionOptions = useMemo(() =>
    Array.from(new Set(documents.map(d => d.jurisdiction).filter((v): v is string => !!v))).sort(),
    [documents]
  );

  // All available types = built-in + user-defined custom + types already in use in documents
  const allDocumentTypes = useMemo(() => {
    const builtIn = DOCUMENT_TYPES.map(t => t.value);
    const inUse = documents.map(d => d.document_type).filter((v): v is string => !!v);
    return Array.from(new Set([...builtIn, ...customTypes, ...inUse])).sort();
  }, [documents, customTypes]);

  // All unique tags in use across all documents
  const tagOptions = useMemo(() => {
    const allTags: string[] = [];
    documents.forEach(d => {
      if (d.tags) {
        d.tags.split(',').forEach(t => {
          const trimmed = t.trim();
          if (trimmed) allTags.push(trimmed);
        });
      }
    });
    return Array.from(new Set(allTags)).sort();
  }, [documents]);

  const fetchDocuments = useCallback(() => {
    const token = window.localStorage.getItem('policyora_token');
    if (!token) return;
    // Wait until the lazy admin fetch completes so currentAdminId is available
    if (secondaryLoading) return;
    // Wait until the auth profile is resolved so currentAdminId is accurate
    if (!adminProfile) return;

    setLoading(true);
    setError(null);
    
    // Fetch documents
    adminApi.getAdminDocuments(token)
      .then(docs => {
        const myDocs = currentAdminId != null
          ? docs.filter(d => d.admin_id === currentAdminId)
          : [];
        setDocuments(myDocs);
      })
      .catch(err => {
        console.error('Failed to load documents:', err);
        setError('Could not load documents. Please try again.');
      })
      .finally(() => setLoading(false));
    
    // Fetch jurisdictions separately
    adminApi.getJurisdictions(token)
      .then(juris => {
        console.log('Jurisdictions loaded:', juris);
        if (juris && juris.length > 0) {
          setJurisdictions(juris);
          setUploadJurisdiction(juris[0].name);
        }
      })
      .catch(err => {
        console.error('Failed to load jurisdictions:', err);
        // Add fallback jurisdictions if API fails
        const fallback = [
          { id: 1, name: 'US Federal' },
          { id: 2, name: 'US State' },
          { id: 3, name: 'European Union' },
        ];
        setJurisdictions(fallback);
        setUploadJurisdiction(fallback[0].name);
      });
  }, [currentAdminId, secondaryLoading, adminProfile]);

  // Load activities from localStorage on mount
  useEffect(() => {
    const savedActivities = localStorage.getItem('document_activities');
    if (savedActivities) {
      try {
        const parsed = JSON.parse(savedActivities);
        const restored = parsed.map((a: any) => ({
          ...a,
          timestamp: new Date(a.timestamp)
        }));
        setActivities(restored);
      } catch (err) {
        console.error('Failed to load saved activities:', err);
      }
    }
  }, []);

  // Persist activities to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('document_activities', JSON.stringify(activities));
  }, [activities]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const addActivity = useCallback((
    type: ActivityType,
    documentName: string,
    adminName: string,
    documentId?: number
  ) => {
    const activity: Activity = {
      id: `${Date.now()}-${Math.random()}`,
      type,
      documentName,
      adminName,
      timestamp: new Date(),
      documentId,
    };
    setActivities(prev => [activity, ...prev].slice(0, 50)); // Keep last 50 activities
  }, []);

  const handleDeleteDocument = useCallback(
    async (documentId: number) => {
      const token = window.localStorage.getItem('policyora_token');
      if (!token) return;

      if (!window.confirm('Delete this document permanently?')) return;

      const docToDelete = documents.find(d => d.id === documentId);
      const docName = docToDelete?.stored_name || docToDelete?.original_filename || 'Document';
      const adminName = docToDelete?.admin_name || 'Unknown';

      try {
        await adminApi.deleteAdminDocument(token, documentId);
        setDocuments(prev => prev.filter(doc => doc.id !== documentId));
        addActivity('delete', docName, adminName, documentId);
      } catch (err) {
        window.alert((err as Error).message || 'Failed to delete document');
      }
    },
    [documents, addActivity],
  );

  const handleUploadDocument = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const token = window.localStorage.getItem('policyora_token');
      if (!token || !uploadFile) return;

      // Validation
      const errors: { [key: string]: string } = {};
      if (!uploadName.trim()) errors.name = 'Please enter document name';
      if (uploadType === 'custom' && !uploadCustomType.trim()) errors.customType = 'Please specify custom type';

      if (Object.keys(errors).length > 0) {
        setUploadErrors(errors);
        return;
      }

      setUploading(true);
      try {
        const newDoc = await adminApi.uploadAdminDocument(token, uploadFile, uploadDesc, {
          document_type: uploadType === 'custom' ? uploadCustomType : uploadType,
          jurisdiction: uploadJurisdiction,
          stored_name: uploadName,
          tags: uploadTags || undefined,
        });
        setDocuments(prev => [newDoc, ...prev]);
        addActivity('upload', uploadName, newDoc.admin_name || 'Unknown', newDoc.id);
        resetUploadForm();
        setShowUploadModal(false);
        window.alert('Document uploaded successfully!');
      } catch (err) {
        window.alert((err as Error).message || 'Failed to upload document');
      } finally {
        setUploading(false);
      }
    },
    [uploadFile, uploadName, uploadType, uploadCustomType, uploadJurisdiction, uploadDesc, uploadTags, addActivity],
  );

  const validateFile = (file: File): string | null => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `File type not allowed. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`;
    }
    if (file.size > 50 * 1024 * 1024) { // 50MB limit
      return 'File size exceeds 50MB limit';
    }
    return null;
  };

  const handleBulkUpload = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const token = window.localStorage.getItem('policyora_token');
      if (!token) return;

      // Validation
      const errors: { [key: string]: string } = {};
      if (uploadFiles.length === 0) errors.files = 'Please select files';
      if (uploadType === 'custom' && !uploadCustomType.trim()) errors.customType = 'Please specify custom type';

      if (Object.keys(errors).length > 0) {
        setUploadErrors(errors);
        return;
      }

      setUploading(true);
      try {
        const uploaded: adminApi.AdminDocument[] = [];
        const fileErrors: { [key: string]: string } = {};

        for (const file of uploadFiles) {
          const fileError = validateFile(file);
          if (fileError) {
            fileErrors[file.name] = fileError;
            continue;
          }

          try {
            const newDoc = await adminApi.uploadAdminDocument(token, file, uploadDesc, {
              document_type: uploadType === 'custom' ? uploadCustomType : uploadType,
              jurisdiction: uploadJurisdiction,
              stored_name: file.name.split('.')[0], // Use filename without extension
              tags: uploadTags || undefined,
            });
            uploaded.push(newDoc);
            addActivity('upload', newDoc.stored_name || newDoc.original_filename, newDoc.admin_name || 'Unknown', newDoc.id);
          } catch (err) {
            fileErrors[file.name] = (err as Error).message || 'Upload failed';
          }
        }

        if (uploaded.length > 0) {
          setDocuments(prev => [...uploaded, ...prev]);
          resetUploadForm();
          setShowUploadModal(false);
          window.alert(`${uploaded.length} of ${uploadFiles.length} documents uploaded successfully!`);
        } else if (Object.keys(fileErrors).length > 0) {
          window.alert('All files failed to upload. Check the errors and try again.');
          setUploadErrors(fileErrors);
        }
      } catch (err) {
        window.alert((err as Error).message || 'Failed to upload documents');
      } finally {
        setUploading(false);
      }
    },
    [uploadFiles, uploadType, uploadCustomType, uploadJurisdiction, uploadDesc, uploadTags, addActivity],
  );

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadFiles([]);
    setUploadType('policy');
    setUploadCustomType('');
    setUploadJurisdiction('US Federal');
    setUploadName('');
    setUploadDesc('');
    setUploadTags('');
    setUploadErrors({});
    setUploadMode('single');
  };

  const handleViewDocument = useCallback((doc: adminApi.AdminDocument) => {
    setViewingDoc(doc);
    setShowViewModal(true);
  }, []);

  const handleDeleteSelected = useCallback(async () => {
    const token = window.localStorage.getItem('policyora_token');
    if (!token || selectedDocs.length === 0) return;

    if (!window.confirm(`Delete ${selectedDocs.length} document(s) permanently?`)) return;

    setLoading(true);
    try {
      const deletedDocs = documents.filter(d => selectedDocs.includes(d.id));
      await Promise.all(selectedDocs.map(id => adminApi.deleteAdminDocument(token, id)));
      setDocuments(prev => prev.filter(doc => !selectedDocs.includes(doc.id)));
      
      // Track each deletion
      deletedDocs.forEach(doc => {
        addActivity('delete', doc.stored_name || doc.original_filename, doc.admin_name || 'Unknown', doc.id);
      });
      
      setSelectedDocs([]);
      window.alert('Documents deleted successfully!');
    } catch (err) {
      window.alert((err as Error).message || 'Failed to delete documents');
    } finally {
      setLoading(false);
    }
  }, [selectedDocs, documents, addActivity]);

  const handleEditDocument = useCallback((doc: adminApi.AdminDocument) => {
    setEditingId(doc.id);
    setEditingName(doc.stored_name || doc.original_filename);
    setEditingType(doc.document_type || '');
    setEditingJurisdiction(doc.jurisdiction || '');
    setEditingDesc(doc.description || '');
    setEditingTags(doc.tags || '');
    setShowEditModal(true);
  }, []);

  const handleEditSave = useCallback(async () => {
    const token = window.localStorage.getItem('policyora_token');
    if (!token || !editingId || !editingName.trim()) return;

    setEditLoading(true);
    try {
      const updated = await adminApi.updateAdminDocument(token, editingId, {
        stored_name: editingName.trim(),
        document_type: editingType || undefined,
        jurisdiction: editingJurisdiction || undefined,
        description: editingDesc || undefined,
        tags: editingTags || '',
      });
      setDocuments(prev => prev.map(doc => doc.id === editingId ? updated : doc));
      setViewingDoc(updated);
      addActivity('edit', editingName.trim(), updated.updated_by_admin_name || updated.admin_name || 'Unknown', editingId);
      setShowEditModal(false);
      setEditLoading(false);
    } catch (err) {
      window.alert((err as Error).message || 'Failed to update document');
      setEditLoading(false);
    }
}, [editingId, editingName, editingType, editingJurisdiction, editingDesc, editingTags, addActivity]);

  const filteredDocuments = useMemo(() => {
    let filtered = documents.filter(doc =>
      doc.original_filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc.stored_name || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (filterType && filterType !== '') {
      filtered = filtered.filter(doc => doc.document_type === filterType);
    }

    if (filterJurisdiction) {
      filtered = filtered.filter(doc => doc.jurisdiction === filterJurisdiction);
    }

    if (filterTag) {
      filtered = filtered.filter(doc => {
        if (!doc.tags) return false;
        return doc.tags.split(',').map(t => t.trim()).includes(filterTag);
      });
    }

    if (sortBy === 'date') {
      filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => a.original_filename.localeCompare(b.original_filename));
    } else if (sortBy === 'size') {
      filtered.sort((a, b) => (b.file_size || 0) - (a.file_size || 0));
    }

    return filtered;
  }, [documents, searchQuery, filterType, filterJurisdiction, filterTag, sortBy]);

  const totalPages = Math.ceil(filteredDocuments.length / docsPerPage);
  const startIdx = (currentPage - 1) * docsPerPage;
  const paginatedDocs = filteredDocuments.slice(startIdx, startIdx + docsPerPage);

  const todayCount = useMemo(() => {
    const today = new Date().toDateString();
    return documents.filter(doc => new Date(doc.created_at).toDateString() === today).length;
  }, [documents]);

  // Calculate document type distribution for pie chart
  const documentTypesData = useMemo(() => {
    const counts: { [key: string]: number } = {};
    documents.forEach(doc => {
      const type = doc.document_type || 'unknown';
      counts[type] = (counts[type] || 0) + 1;
    });
    
    const total = documents.length || 1;
    const colors = {
      'policy': 'var(--primary-500)',
      'legal_docs': 'var(--ds-success)',
      'reports': 'var(--ds-brand-blue)',
      'custom': 'var(--primary-500)',
      'unknown': 'var(--ds-text-muted)'
    };
    
    const labels: { [key: string]: string } = {
      'policy': 'Policy Briefs',
      'legal_docs': 'Legal Documents',
      'reports': 'Reports',
      'custom': 'Others',
      'unknown': 'Uncategorized'
    };

    // Always include all 4 main types, even if count is 0
    const mainTypes = ['policy', 'legal_docs', 'reports', 'custom'];
    const types = mainTypes
      .map(type => {
        const count = counts[type] || 0;
        return {
          type,
          count,
          percentage: total > 0 ? Math.round((count / total) * 100) : 0,
          color: colors[type as keyof typeof colors],
          label: labels[type as keyof typeof labels]
        };
      })
      .sort((a, b) => b.count - a.count);

    return types;
  }, [documents]);

  // Generate SVG pie chart path
  const pieChartSVG = useMemo(() => {
    if (documentTypesData.length === 0 || documentTypesData.every(t => t.count === 0)) return null;

    let currentAngle = 0;
    const radius = 40;
    const circumference = 2 * Math.PI * radius;

    return documentTypesData
      .filter(t => t.count > 0) // Only render segments with actual documents
      .map(({ type, percentage, color }) => {
        const percentageDecimal = percentage / 100;
        const sliceLength = circumference * percentageDecimal;
        const sliceDashOffset = -currentAngle * circumference / 360;
        
        const segment = (
          <circle
            key={type}
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="15"
            strokeDasharray={`${sliceLength} ${circumference}`}
            strokeDashoffset={sliceDashOffset}
          />
        );
        
        currentAngle += (percentage * 3.6);
        return segment;
      });
  }, [documentTypesData]);

  const toggleDocumentSelection = (docId: number) => {
    setSelectedDocs(prev => 
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const toggleAllDocuments = () => {
    if (selectedDocs.length === paginatedDocs.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(paginatedDocs.map(doc => doc.id));
    }
  };

  const handleAddCustomType = () => {
    const trimmed = newTypeName.trim().toLowerCase().replace(/\s+/g, '_');
    if (!trimmed) { setManageTypesError('Please enter a type name'); return; }
    if (allDocumentTypes.includes(trimmed)) { setManageTypesError('Type already exists'); return; }
    const updated = [...customTypes, trimmed];
    setCustomTypes(updated);
    localStorage.setItem('admin_custom_doc_types', JSON.stringify(updated));
    setNewTypeName('');
    setManageTypesError(null);
  };

  const handleDeleteType = async (typeName: string) => {
    const token = window.localStorage.getItem('policyora_token');
    if (!token) return;
    const docsWithType = documents.filter(d => d.document_type === typeName).length;
    const msg = docsWithType > 0
      ? `Delete type "${typeName}"? This will clear it from ${docsWithType} document(s).`
      : `Delete type "${typeName}"?`;
    if (!window.confirm(msg)) return;
    setDeletingType(typeName);
    try {
      await adminApi.deleteAdminDocumentType(token, typeName);
      setDocuments(prev => prev.map(d => d.document_type === typeName ? { ...d, document_type: null } : d));
    } catch (err) {
      window.alert((err as Error).message || 'Failed to delete type');
    } finally {
      setDeletingType(null);
    }
    // Remove from custom types list too
    const updatedCustom = customTypes.filter(t => t !== typeName);
    if (updatedCustom.length !== customTypes.length) {
      setCustomTypes(updatedCustom);
      localStorage.setItem('admin_custom_doc_types', JSON.stringify(updatedCustom));
    }
  };

  return (
    <section className={styles.documentsSection}>
      <div className={styles.pageHeader}>
        <div>
          <Eyebrow tone="default" dot>DOCUMENTS</Eyebrow>
          <PageHeading
            accentWord="Management"
            accentTone="orange"
            size="h2"
            align="left"
            level="h2"
            className={styles.pageTitle}
          >
            Document Management
          </PageHeading>
        </div>
        <div className={styles.pageTools}>
          <button
            className={styles.uploadBtn}
            onClick={() => navigate('/admin/dashboard/synced-docs')}
          >
            View synced docs/transcripts
          </button>
          <button
            className={styles.uploadBtn}
            onClick={() => setShowManageTypesModal(true)}
          >
            <Settings size={16} aria-hidden /> Manage Types
          </button>
          <button className={styles.uploadBtn} onClick={() => setShowUploadModal(true)}>+ Upload Document</button>
          {selectedDocs.length > 0 && (
            <button className={styles.bulkActionsBtn} onClick={handleDeleteSelected} style={{ background: 'var(--ds-danger)', color: 'var(--ds-text-on-dark)', border: 'none' }}>
              Delete Selected ({selectedDocs.length})
            </button>
          )}
        </div>
      </div>

      <div className={styles.kpiGrid} style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        <div className={`${metricsStyles.metricCard} ${metricsStyles.metric_gold}`}>
          <span className={metricsStyles.metricIconBadge}><FileText size={20} aria-hidden /></span>
          <div className={metricsStyles.metricContent}>
            <div className={metricsStyles.metricLabel}>Total Documents</div>
            <div className={metricsStyles.metricValue}>{documents.length.toLocaleString()}</div>
            <div className={metricsStyles.metricMeta}>All stored documents</div>
          </div>
        </div>

        <div className={`${metricsStyles.metricCard} ${metricsStyles.metric_pink}`}>
          <span className={metricsStyles.metricIconBadge}><Upload size={20} aria-hidden /></span>
          <div className={metricsStyles.metricContent}>
            <div className={metricsStyles.metricLabel}>Uploaded Today</div>
            <div className={metricsStyles.metricValue}>{todayCount}</div>
            <div className={metricsStyles.metricMeta}>New uploads in last 24h</div>
          </div>
        </div>
      </div>

      <div className={styles.controlsRow}>
        <div className={styles.searchWrapper}>
          <span className={styles.searchIcon}><Search size={16} aria-hidden /></span>
          <input
            type="text"
            placeholder="Search by document name..."
            className={styles.searchInput}
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        <div className={styles.selectWrap} style={{ minWidth: 180 }}>
          <select className={styles.filterSelect} value={filterType} onChange={(e) => {
            setFilterType(e.target.value);
            setCurrentPage(1);
          }}>
            <option value="">Filter document type</option>
            {allDocumentTypes.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className={styles.selectWrap} style={{ minWidth: 180 }}>
          <select className={styles.filterSelect} value={filterJurisdiction} onChange={(e) => {
            setFilterJurisdiction(e.target.value);
            setCurrentPage(1);
          }}>
            <option value="">Filter jurisdiction</option>
            {jurisdictionOptions.map(j => (
              <option key={j} value={j}>{j}</option>
            ))}
          </select>
        </div>
        {tagOptions.length > 0 && (
          <div className={styles.selectWrap} style={{ minWidth: 160 }}>
            <select className={styles.filterSelect} value={filterTag} onChange={(e) => {
              setFilterTag(e.target.value);
              setCurrentPage(1);
            }}>
              <option value="">Filter tag</option>
              {tagOptions.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.docsTable}>
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  checked={paginatedDocs.length > 0 && selectedDocs.length === paginatedDocs.length}
                  onChange={toggleAllDocuments}
                />
              </th>
              <th>Document Name</th>
              <th>Type</th>
              <th>Jurisdiction</th>
              <th>Tags</th>
              <th>Upload Date</th>
              <th>Size</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className={styles.emptyState}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem' }}>
                    <div style={{ width: 18, height: 18, border: '2px solid var(--ds-border)', borderTopColor: 'var(--primary-500)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                    Loading documents…
                  </div>
                </td>
              </tr>
            ) : paginatedDocs.length === 0 ? (
              <tr>
                <td colSpan={9} className={styles.emptyState}>No documents found</td>
              </tr>
            ) : (
              paginatedDocs.map(doc => (
                <tr key={doc.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedDocs.includes(doc.id)}
                      onChange={() => toggleDocumentSelection(doc.id)}
                    />
                  </td>
                  <td><span className={styles.docName}>{doc.stored_name || doc.original_filename}</span></td>
                  <td>{doc.document_type || 'N/A'}</td>
                  <td>{doc.jurisdiction || 'N/A'}</td>
                  <td>
                    {doc.tags ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {parseTags(doc.tags).map(tag => <TagChip key={tag} tag={tag} />)}
                      </div>
                    ) : (
                      <span style={{ color: 'var(--admin-text-muted,var(--ds-text-faint))', fontSize: '0.82rem' }}>—</span>
                    )}
                  </td>
                  <td>{new Date(doc.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                  <td>{doc.file_size ? `${(doc.file_size / 1024 / 1024).toFixed(1)} MB` : 'N/A'}</td>
                  <td><span className={styles.badge + ' ' + styles.badgePublished}>Published</span></td>
                  <td className={styles.docActions}>
                    <button className={styles.actionIcon} onClick={() => handleViewDocument(doc)} title="View"><Eye size={16} aria-hidden /></button>
                    <button className={styles.actionIcon} onClick={() => handleEditDocument(doc)} title="Edit"><Pencil size={16} aria-hidden /></button>
                    <button className={styles.actionIcon} onClick={() => downloadFile(doc.download_url, doc.original_filename)} title="Download">↓</button>
                    <button className={styles.actionIcon + ' ' + styles.dangerIcon} onClick={() => handleDeleteDocument(doc.id)} title="Delete">✕</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.paginationBar}>
        <span>Showing {paginatedDocs.length > 0 ? startIdx + 1 : 0}-{Math.min(startIdx + docsPerPage, filteredDocuments.length)} of {filteredDocuments.length}</span>
        <div className={styles.pageControls}>
          <span>Page</span>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              className={currentPage === page ? styles.pageNumActive : styles.pageNum}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          {totalPages > 5 && <span>...</span>}
          {totalPages > 5 && <button className={styles.pageNum} onClick={() => setCurrentPage(totalPages)}>[{totalPages}]</button>}
          <button className={styles.nextBtn} onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}>Next</button>
        </div>
      </div>

      <div className={styles.bottomGrid}>
        <div className={styles.recentSection}>
          <h3>Recent Activity</h3>
          <div className={styles.recentListScrollable}>
            {activities.length > 0 ? (
              activities.slice(0, recentLimit).map(activity => {
                const mins = Math.floor((Date.now() - activity.timestamp.getTime()) / 60000);
                let relative: string;
                if (mins < 60) {
                  relative = `${mins} min ago`;
                } else if (mins < 60 * 24) {
                  const hrs = Math.floor(mins / 60);
                  relative = `${hrs} hr${hrs > 1 ? 's' : ''} ago`;
                } else {
                  const days = Math.floor(mins / (60 * 24));
                  if (days < 30) {
                    relative = `${days} day${days > 1 ? 's' : ''} ago`;
                  } else {
                    const months = Math.floor(days / 30);
                    relative = `${months} month${months > 1 ? 's' : ''} ago`;
                  }
                }

                const typeLabel = activity.type === 'upload' ? 'Uploaded' : activity.type === 'edit' ? 'Edited' : activity.type === 'delete' ? 'Deleted' : 'Accessed';
                const iconClass = activity.type === 'upload' ? 'upload' : activity.type === 'edit' ? 'edit' : activity.type === 'delete' ? 'delete' : 'upload';
                return (
                  <div key={activity.id} className={styles.recentItem}>
                    <div className={`${styles.recentIcon} ${styles[iconClass]}`} aria-hidden>
                      {activity.type === 'upload' ? <Upload size={16} aria-hidden /> : activity.type === 'edit' ? <Pencil size={16} aria-hidden /> : activity.type === 'delete' ? '✕' : <Info size={16} aria-hidden />}
                    </div>
                    <div className={styles.recentText}>
                      <div className={styles.recentName}>{activity.documentName}</div>
                      <div className={styles.recentMeta}>{typeLabel} by <strong>{activity.adminName}</strong></div>
                    </div>
                    <div className={styles.recentTime}>{relative}</div>
                  </div>
                );
              })
            ) : (
              <p style={{ color: 'var(--ds-text-faint)', fontSize: '0.9rem', padding: '1rem' }}>No recent activities</p>
            )}

            {activities.length > 3 && (
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className={styles.showAllBtn}
                  onClick={() => window.open('/admin/documents/activity', '_blank')}
                >
                  {`Show all (${activities.length})`}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={styles.categoriesSection}>
          <h3>Document Categories</h3>
          <div className={styles.pieChartContainer}>
            {documents.length > 0 ? (
              <svg className={styles.pieChart} viewBox="0 0 100 100">
                {pieChartSVG}
              </svg>
            ) : (
              <p style={{ color: 'var(--ds-text-faint)', fontSize: '0.9rem', textAlign: 'center', padding: '2rem' }}>No documents yet</p>
            )}
          </div>
          <div className={styles.categoryLegend}>
            {documentTypesData.length > 0 ? (
              documentTypesData.map(({ type, label, color, percentage, count }) => (
                <div key={type}>
                  <span style={{ background: color }}></span>
                  {label} ({percentage}% - {count} {count === 1 ? 'doc' : 'docs'})
                </div>
              ))
            ) : (
              <p style={{ color: 'var(--ds-text-faint)', fontSize: '0.9rem' }}>No documents to categorize</p>
            )}
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className={styles.modalOverlay} onClick={() => setShowUploadModal(false)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className={styles.modalHeader}>
              <h2>Upload Document</h2>
              <button className={styles.modalClose} onClick={() => {
                setShowUploadModal(false);
                resetUploadForm();
              }}>✕</button>
            </div>

            {/* Upload Mode Tabs */}
            <div className={styles.uploadTabs}>
              <button
                className={uploadMode === 'single' ? styles.tabActive : styles.tab}
                onClick={() => { setUploadMode('single'); setUploadErrors({}); }}
              >
                Single Upload
              </button>
              <button
                className={uploadMode === 'bulk' ? styles.tabActive : styles.tab}
                onClick={() => { setUploadMode('bulk'); setUploadErrors({}); }}
              >
                Bulk Upload
              </button>
            </div>

            {uploadMode === 'single' ? (
              <form onSubmit={handleUploadDocument}>
                <div className={styles.formContent}>
                  {/* Document Type */}
                  <div className={styles.formGroup}>
                    <label htmlFor="docType">Document Type *</label>
                    <div className={styles.selectWrap}>
                      <select
                        id="docType"
                        value={uploadType}
                        onChange={(e) => setUploadType(e.target.value as DocumentType)}
                        className={uploadErrors.type ? styles.inputError : ''}
                      >
                        {allDocumentTypes.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    {uploadErrors.type && <span className={styles.errorText}>{uploadErrors.type}</span>}
                  </div>

                  {/* Custom Type Input */}
                  {uploadType === 'custom' && (
                    <div className={styles.formGroup}>
                      <label htmlFor="customType">Specify Type *</label>
                      <input
                        id="customType"
                        type="text"
                        value={uploadCustomType}
                        onChange={(e) => setUploadCustomType(e.target.value)}
                        placeholder="e.g., Compliance Guide, Technical Manual..."
                        className={uploadErrors.customType ? styles.inputError : ''}
                      />
                      {uploadErrors.customType && <span className={styles.errorText}>{uploadErrors.customType}</span>}
                    </div>
                  )}

                  {/* Jurisdiction */}
                  <div className={styles.formGroup}>
                    <label htmlFor="jurisdiction">Jurisdiction *</label>
                    <div className={styles.selectWrap}>
                      <select
                        id="jurisdiction"
                        value={uploadJurisdiction}
                        onChange={(e) => setUploadJurisdiction(e.target.value)}
                      >
                        {jurisdictions.length === 0 ? (
                          <option value="">Loading jurisdictions...</option>
                        ) : (
                          jurisdictions.map(j => (
                            <option key={j.id} value={j.name}>{j.name}</option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>

                  {/* Document Name */}
                  <div className={styles.formGroup}>
                    <label htmlFor="docName">Document Name *</label>
                    <input
                      id="docName"
                      type="text"
                      value={uploadName}
                      onChange={(e) => setUploadName(e.target.value)}
                      placeholder="e.g., Q1 2026 Policy Brief, Legal Framework..."
                      className={uploadErrors.name ? styles.inputError : ''}
                    />
                    {uploadErrors.name && <span className={styles.errorText}>{uploadErrors.name}</span>}
                  </div>

                  {/* File Upload */}
                  <div className={styles.formGroup}>
                    <label htmlFor="file">Document File * (PDF, DOC, DOCX, TXT, XLSX - Max 50MB)</label>
                    <input
                      id="file"
                      type="file"
                      accept=".pdf,.doc,.docx,.txt,.xlsx,.xls"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const err = validateFile(file);
                          if (err) {
                            setUploadErrors({ ...uploadErrors, file: err });
                            setUploadFile(null);
                          } else {
                            setUploadErrors({ ...uploadErrors, file: '' });
                            setUploadFile(file);
                            // Carry the filename into the (required) document-name
                            // field so it's shown/saved by default instead of the
                            // user having to retype it.
                            if (!uploadName.trim()) setUploadName(file.name.replace(/\.[^.]+$/, ''));
                          }
                        }
                      }}
                      className={uploadErrors.file ? styles.inputError : ''}
                    />
                    {uploadFile && <span className={styles.successText}>✓ {uploadFile.name}</span>}
                    {uploadErrors.file && <span className={styles.errorText}>{uploadErrors.file}</span>}
                  </div>

                  {/* Description */}
                  <div className={styles.formGroup}>
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      value={uploadDesc}
                      onChange={(e) => setUploadDesc(e.target.value)}
                      placeholder="Optional: Add any notes about this document..."
                      rows={3}
                    />
                  </div>

                  {/* Tags */}
                  <div className={styles.formGroup}>
                    <label htmlFor="uploadTags">Tags <span style={{ fontWeight: 400, color: 'var(--admin-text-muted,var(--ds-text-faint))' }}>(optional)</span></label>
                    <input
                      id="uploadTags"
                      type="text"
                      value={uploadTags}
                      onChange={(e) => setUploadTags(e.target.value)}
                      placeholder="e.g. urgent, reviewed, 2026 (comma-separated)"
                    />
                    {parseTags(uploadTags).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.4rem' }}>
                        {parseTags(uploadTags).map(tag => <TagChip key={tag} tag={tag} size="md" />)}
                      </div>
                    )}
                    <span style={{ fontSize: '0.78rem', color: 'var(--admin-text-muted,var(--ds-text-faint))', marginTop: '0.35rem', display: 'block' }}>Separate multiple tags with commas for easy filtering later.</span>
                  </div>
                </div>

                <div className={styles.modalActions}>
                  <button
                    type="button"
                    className={styles.cancelBtn}
                    onClick={() => {
                      setShowUploadModal(false);
                      resetUploadForm();
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className={styles.submitBtn} disabled={uploading || !uploadFile}>
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </form>
            ) : (
              /* Bulk Upload Form */
              <form onSubmit={handleBulkUpload}>
                <div className={styles.formContent}>
                  {/* Document Type */}
                  <div className={styles.formGroup}>
                    <label htmlFor="bulkDocType">Document Type for All Files *</label>
                    <div className={styles.selectWrap}>
                      <select
                        id="bulkDocType"
                        value={uploadType}
                        onChange={(e) => setUploadType(e.target.value as DocumentType)}
                      >
                        {allDocumentTypes.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Custom Type Input */}
                  {uploadType === 'custom' && (
                    <div className={styles.formGroup}>
                      <label htmlFor="bulkCustomType">Specify Type *</label>
                      <input
                        id="bulkCustomType"
                        type="text"
                        value={uploadCustomType}
                        onChange={(e) => setUploadCustomType(e.target.value)}
                        placeholder="e.g., Compliance Guide, Technical Manual..."
                        className={uploadErrors.customType ? styles.inputError : ''}
                      />
                      {uploadErrors.customType && <span className={styles.errorText}>{uploadErrors.customType}</span>}
                    </div>
                  )}

                  {/* Jurisdiction */}
                  <div className={styles.formGroup}>
                    <label htmlFor="bulkJurisdiction">Jurisdiction *</label>
                    <div className={styles.selectWrap}>
                      <select
                        id="bulkJurisdiction"
                        value={uploadJurisdiction}
                        onChange={(e) => setUploadJurisdiction(e.target.value)}
                      >
                        {jurisdictions.length === 0 ? (
                          <option value="">Loading jurisdictions...</option>
                        ) : (
                          jurisdictions.map(j => (
                            <option key={j.id} value={j.name}>{j.name}</option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>

                  {/* Bulk File Upload */}
                  <div className={styles.formGroup}>
                    <label htmlFor="bulkFiles">Select Files * (PDF, DOC, DOCX, TXT, XLSX - Max 50MB each)</label>
                    <div className={styles.bulkUploadArea}>
                      <input
                        id="bulkFiles"
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.txt,.xlsx,.xls"
                        onChange={(e) => setUploadFiles(Array.from(e.target.files || []))}
                      />
                      <div className={styles.uploadAreaText}>
                        <p><Folder size={16} aria-hidden /> Click to select files or drag and drop</p>
                      </div>
                    </div>
                  </div>

                  {/* File List */}
                  {uploadFiles.length > 0 && (
                    <div className={styles.fileList}>
                      <p className={styles.fileListTitle}>Selected Files ({uploadFiles.length})</p>
                      {uploadFiles.map((file, idx) => {
                        const err = validateFile(file);
                        return (
                          <div key={idx} className={styles.fileItem + (err ? ' ' + styles.fileItemError : '')}>
                            <span className={styles.fileName}><FileText size={16} aria-hidden /> {file.name}</span>
                            <span className={styles.fileSize}>
                              {(file.size / 1024 / 1024).toFixed(1)} MB
                            </span>
                            {err && <span className={styles.fileError}><XCircle size={16} aria-hidden /> {err}</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Description */}
                  <div className={styles.formGroup}>
                    <label htmlFor="bulkDescription">Description (applies to all files)</label>
                    <textarea
                      id="bulkDescription"
                      value={uploadDesc}
                      onChange={(e) => setUploadDesc(e.target.value)}
                      placeholder="Optional: Add notes about these documents..."
                      rows={3}
                    />
                  </div>

                  {/* Tags */}
                  <div className={styles.formGroup}>
                    <label htmlFor="bulkUploadTags">Tags <span style={{ fontWeight: 400, color: 'var(--admin-text-muted,var(--ds-text-faint))' }}>(optional, applies to all files)</span></label>
                    <input
                      id="bulkUploadTags"
                      type="text"
                      value={uploadTags}
                      onChange={(e) => setUploadTags(e.target.value)}
                      placeholder="e.g. urgent, reviewed, 2026 (comma-separated)"
                    />
                    {parseTags(uploadTags).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.4rem' }}>
                        {parseTags(uploadTags).map(tag => <TagChip key={tag} tag={tag} size="md" />)}
                      </div>
                    )}
                    <span style={{ fontSize: '0.78rem', color: 'var(--admin-text-muted,var(--ds-text-faint))', marginTop: '0.35rem', display: 'block' }}>Separate multiple tags with commas for easy filtering later.</span>
                  </div>
                </div>

                <div className={styles.modalActions}>
                  <button
                    type="button"
                    className={styles.cancelBtn}
                    onClick={() => {
                      setShowUploadModal(false);
                      resetUploadForm();
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className={styles.submitBtn} disabled={uploading || uploadFiles.length === 0}>
                    {uploading ? 'Uploading...' : `Upload ${uploadFiles.length} Files`}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingDoc && (
        <div className={styles.modalOverlay} onClick={() => setShowViewModal(false)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Document Details</h2>
              <button className={styles.modalClose} onClick={() => setShowViewModal(false)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Name:</span>
                <span className={styles.detailValue}>{viewingDoc.stored_name || viewingDoc.original_filename}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Size:</span>
                <span className={styles.detailValue}>{viewingDoc.file_size ? `${(viewingDoc.file_size / 1024 / 1024).toFixed(1)} MB` : 'N/A'}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Type:</span>
                <span className={styles.detailValue}>{viewingDoc.document_type || 'Document'}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Jurisdiction:</span>
                <span className={styles.detailValue}>{viewingDoc.jurisdiction || 'N/A'}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Uploaded by:</span>
                <span className={styles.detailValue}>{viewingDoc.admin_name || 'Unknown'}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Uploaded:</span>
                <span className={styles.detailValue}>{new Date(viewingDoc.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              {viewingDoc.updated_by_admin_name && viewingDoc.updated_at && (
                <>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Last edited by:</span>
                    <span className={styles.detailValue}>{viewingDoc.updated_by_admin_name}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Edited:</span>
                    <span className={styles.detailValue}>{new Date(viewingDoc.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </>
              )}
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Description:</span>
                <span className={styles.detailValue}>{viewingDoc.description || 'No description'}</span>
              </div>
              {viewingDoc.tags && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Tags:</span>
                  <span className={styles.detailValue}>
                    <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                      {parseTags(viewingDoc.tags).map(tag => <TagChip key={tag} tag={tag} size="md" />)}
                    </span>
                  </span>
                </div>
              )}
            </div>
            <div className={styles.modalActions}>
              <button type="button" className={styles.cancelBtn} onClick={() => setShowViewModal(false)}>Close</button>
              <button type="button" className={styles.submitBtn} onClick={() => window.open(viewingDoc.download_url, '_blank')}>
                Preview
              </button>
              <button type="button" className={styles.submitBtn} onClick={() => handleEditDocument(viewingDoc)}>Edit</button>
            </div>

          </div>
        </div>
      )}
      
      {showEditModal && editingId && (
        <div className={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Edit Document</h2>
              <button className={styles.modalClose} onClick={() => setShowEditModal(false)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Document Name:</span>
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  placeholder="Enter document name"
                  className={styles.editInput}
                  disabled={editLoading}
                />
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Document Type:</span>
                <div className={styles.selectWrap} style={{ flex: 1 }}>
                  <select
                    value={editingType}
                    onChange={(e) => setEditingType(e.target.value)}
                    className={styles.editInput}
                    disabled={editLoading}
                  >
                    <option value="">Select type</option>
                    {allDocumentTypes.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Jurisdiction:</span>
                <div className={styles.selectWrap} style={{ flex: 1 }}>
                  <select
                    value={editingJurisdiction}
                    onChange={(e) => setEditingJurisdiction(e.target.value)}
                    className={styles.editInput}
                    disabled={editLoading}
                  >
                    <option value="">Select jurisdiction</option>
                    {jurisdictions.map(j => (
                      <option key={j.id} value={j.name}>{j.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Description:</span>
                <textarea
                  value={editingDesc}
                  onChange={(e) => setEditingDesc(e.target.value)}
                  placeholder="Enter document description"
                  className={styles.editInput}
                  disabled={editLoading}
                  rows={3}
                />
              </div>
              <div className={styles.detailRow} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.4rem' }}>
                <span className={styles.detailLabel}>Tags:</span>
                <input
                  type="text"
                  value={editingTags}
                  onChange={(e) => setEditingTags(e.target.value)}
                  placeholder="e.g. urgent, reviewed, 2026 (comma-separated)"
                  className={styles.editInput}
                  disabled={editLoading}
                  style={{ width: '100%' }}
                />
                {parseTags(editingTags).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                    {parseTags(editingTags).map(tag => <TagChip key={tag} tag={tag} size="md" />)}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.modalActions}>
              <button type="button" className={styles.cancelBtn} onClick={() => setShowEditModal(false)} disabled={editLoading}>Cancel</button>
              <button type="button" className={styles.submitBtn} onClick={handleEditSave} disabled={editLoading || !editingName.trim()}>
                {editLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Manage Types Modal ── */}
      {showManageTypesModal && (
        <div className={styles.modalOverlay} role="dialog" aria-modal="true">
          <div className={styles.modalContent} style={{ maxWidth: 520 }}>
            <div className={styles.modalHeader}>
              <h3 style={{ margin: 0 }}>Manage Document Types</h3>
              <button className={styles.modalClose} onClick={() => { setShowManageTypesModal(false); setNewTypeName(''); setManageTypesError(null); }}>✕</button>
            </div>
            <div className={styles.modalBody}>
              {/* Add new type */}
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600, fontSize: '0.93rem' }}>Add new type</p>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={newTypeName}
                    onChange={(e) => { setNewTypeName(e.target.value); setManageTypesError(null); }}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddCustomType()}
                    placeholder="e.g. compliance_report"
                    style={{ flex: 1, padding: '0.4rem 0.6rem', border: '1px solid var(--admin-border,var(--ds-border))', borderRadius: 6, background: 'var(--admin-surface,#fff)', color: 'var(--admin-text,var(--ds-text))' }}
                  />
                  <button
                    type="button"
                    className={styles.submitBtn}
                    onClick={handleAddCustomType}
                    style={{ padding: '0.4rem 0.8rem', whiteSpace: 'nowrap' }}
                  >
                    + Add
                  </button>
                </div>
                {manageTypesError && <p style={{ color: 'var(--ds-danger-text)', fontSize: '0.85rem', margin: '0.35rem 0 0 0' }}>{manageTypesError}</p>}
                <p style={{ margin: '0.35rem 0 0 0', fontSize: '0.82rem', color: 'var(--admin-text-muted,var(--ds-text-faint))' }}>Spaces will be converted to underscores.</p>
              </div>

              {/* All types list */}
              <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600, fontSize: '0.93rem' }}>All types</p>
              {allDocumentTypes.length === 0 ? (
                <p style={{ color: 'var(--admin-text-muted,var(--ds-text-faint))', fontSize: '0.9rem' }}>No types defined yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: 320, overflowY: 'auto' }}>
                  {allDocumentTypes.map(typeName => {
                    const count = documents.filter(d => d.document_type === typeName).length;
                    const isBuiltIn = DOCUMENT_TYPES.some(t => t.value === typeName);
                    return (
                      <div
                        key={typeName}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.45rem 0.65rem', border: '1px solid var(--admin-border,var(--ds-border))', borderRadius: 8, background: 'var(--admin-surface,#fff)' }}
                      >
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600, fontSize: '0.93rem' }}>{typeName}</span>
                          {isBuiltIn && <span style={{ fontSize: '0.75rem', background: 'rgba(68,76,231,0.09)', color: 'var(--ds-info-text)', padding: '0.1rem 0.4rem', borderRadius: 999, border: '1px solid rgba(68,76,231,0.15)' }}>built-in</span>}
                          <span style={{ fontSize: '0.82rem', color: 'var(--admin-text-muted,var(--ds-text-muted))' }}>{count} doc{count !== 1 ? 's' : ''}</span>
                        </div>
                        <button
                          type="button"
                          disabled={deletingType === typeName}
                          onClick={() => handleDeleteType(typeName)}
                          style={{ background: 'transparent', border: '1px solid rgba(240, 68, 56, 0.25)', color: 'var(--ds-danger-text)', padding: '0.22rem 0.5rem', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem' }}
                        >
                          {deletingType === typeName ? '…' : 'Delete'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className={styles.modalActions}>
              <button type="button" className={styles.cancelBtn} onClick={() => { setShowManageTypesModal(false); setNewTypeName(''); setManageTypesError(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
